// Packages
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
} from "@stencil/core";

/**
 * `fx-button` is a headless, accessible button component. It exposes every
 * styleable surface via `part` attributes and its state through `data-*`
 * attributes so themes can customize it without internal styles.
 *
 * @slot - The button label.
 * @slot start - Content rendered before the label, typically an icon.
 * @slot end - Content rendered after the label, typically an icon.
 *
 * @part base - The visible native button element.
 * @part prefix - The wrapper around the `start` slot.
 * @part label - The wrapper around the default slot.
 * @part suffix - The wrapper around the `end` slot.
 * @part native - The hidden native button used to trigger form submission.
 */
// `styleUrl` is intentionally omitted (no internal styles per AGENTS.md).
@Component({
  tag: "fx-button",
  shadow: true,
})
export class FxButton {
  // --------------------------------------------
  // Element / internal references
  // --------------------------------------------

  @Element() el!: HTMLElement;

  private nativeButton?: HTMLButtonElement;

  // --------------------------------------------
  // State
  // --------------------------------------------

  /**
   * Tracks whether the button currently has focus so the `focused` class can
   * be toggled on the host.
   */
  @State() isFocused = false;

  /**
   * Tracks whether the button is currently pressed so the `activatable` class
   * can be toggled on the host.
   */
  @State() isActive = false;

  // --------------------------------------------
  // Props
  // --------------------------------------------

  /**
   * @prop - The native button type. Can be `button`, `submit`, or `reset`.
   */
  @Prop()
  type: "button" | "submit" | "reset" = "button";

  /**
   * @prop - The size of the button. Reflects to `data-size` on the host.
   */
  @Prop()
  size: "xs" | "sm" | "md" | "lg" | "xl" = "md";

  /**
   * @prop - The visual fill of the button. `solid` is the default filled style,
   * `outline` shows a border, `clear` is text-only, and `link` is a text-only
   * style with an underline. Reflects to `data-fill` on the host.
   */
  @Prop()
  fill: "solid" | "outline" | "clear" | "link" = "solid";

  /**
   * @prop - If `true`, the button is disabled and cannot be interacted with.
   * Reflects to the `disabled` attribute on the host.
   */
  @Prop({ reflect: true })
  disabled: boolean = false;

  /**
   * @prop - If `true`, the button is rendered with a circular shape. Reflects
   * to `data-circle` on the host.
   */
  @Prop()
  isCircle: boolean = false;

  /**
   * @prop - When `block`, the button fills the width of its container. When
   * `full`, the button fills the width and removes horizontal borders and
   * border radius. Reflects to `data-expand` on the host.
   */
  @Prop()
  expand?: "block" | "full";

  /**
   * @prop - The form to associate the button with. Can be a form id or an
   * `HTMLFormElement` reference.
   */
  @Prop()
  form?: string | HTMLFormElement;

  // --------------------------------------------
  // Events
  // --------------------------------------------

  /**
   * @event - Emitted when the button receives focus.
   */
  @Event()
  fxFocus!: EventEmitter<void>;

  /**
   * @event - Emitted when the button loses focus.
   */
  @Event()
  fxBlur!: EventEmitter<void>;

  // --------------------------------------------
  // Private handlers
  // --------------------------------------------

  private handleFocus = () => {
    if (this.disabled) {
      return;
    }
    this.isFocused = true;
    this.fxFocus.emit();
  };

  private handleBlur = () => {
    this.isFocused = false;

    if (this.disabled) {
      return;
    }

    this.fxBlur.emit();
  };

  private handlePointerDown = () => {
    if (this.disabled) {
      return;
    }
    this.isActive = true;
  };

  private handlePointerUp = () => {
    this.isActive = false;
  };

  private handlePointerLeave = () => {
    this.isActive = false;
  };

  private handleClick = (ev: MouseEvent) => {
    if (this.disabled) {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }

    if (this.type === "button") {
      return;
    }

    this.nativeButton?.click();
  };

  private handleNativeClick = () => {
    if (this.disabled) {
      return;
    }

    const form = this.findForm();
    if (!form) {
      return;
    }

    if (this.type === "submit") {
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
      } else {
        this.submitFormWithNativeButton(form);
      }
    } else if (this.type === "reset") {
      form.reset();
    }
  };

  private findForm(): HTMLFormElement | null {
    if (this.form instanceof HTMLFormElement) {
      return this.form;
    }

    if (typeof this.form === "string") {
      const form = document.getElementById(this.form);
      if (form instanceof HTMLFormElement) {
        return form;
      }
    }

    return this.el.closest("form") as HTMLFormElement | null;
  }

  private submitFormWithNativeButton(form: HTMLFormElement): void {
    const button = this.nativeButton?.cloneNode(true) as HTMLButtonElement;
    if (!button) {
      return;
    }

    button.setAttribute("type", "submit");
    button.style.position = "absolute";
    button.style.width = "0px";
    button.style.height = "0px";
    button.style.opacity = "0";
    button.style.border = "0";
    button.style.padding = "0";
    button.style.margin = "0";
    button.style.overflow = "hidden";

    form.appendChild(button);
    button.click();
    form.removeChild(button);
  }

  // --------------------------------------------
  // Render
  // --------------------------------------------

  render() {
    const {
      type,
      size,
      fill,
      disabled,
      isCircle,
      expand,
      isFocused,
      isActive,
    } = this;

    return (
      <Host
        data-size={size}
        data-fill={fill}
        data-expand={expand}
        data-circle={isCircle ? "" : undefined}
        aria-disabled={disabled ? "true" : null}
        class={{
          activatable: isActive,
          focused: isFocused,
        }}
      >
        <button
          part="base"
          type={type}
          disabled={disabled}
          tabIndex={disabled ? -1 : 0}

          onClick={this.handleClick}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onPointerDown={this.handlePointerDown}
          onPointerUp={this.handlePointerUp}
          onPointerLeave={this.handlePointerLeave}
          onMouseDown={this.handlePointerDown}
          onMouseUp={this.handlePointerUp}
          onMouseLeave={this.handlePointerLeave}
        >
          <span part="prefix">
            <slot name="start" />
          </span>
          <span part="label">
            <slot />
          </span>
          <span part="suffix">
            <slot name="end" />
          </span>
        </button>

        <button
          part="native"
          type={type}
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
          ref={(el) => (this.nativeButton = el as HTMLButtonElement)}
          onClick={this.handleNativeClick}
        />
      </Host>
    );
  }
}
